import { AxiosResponse } from 'axios';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import qs from 'query-string';
import axios from 'libs/Axios';
import { AxiosDefaultErrorEntity } from 'types/api/Common/ErrorTypes';
import { CubeMemberMatchPreviewResponse } from 'types/api/SystemTenant/AROCube/CubeBuilderTypes';
import {
  MemberMappingDefinition,
  MemberMappingDefinitionInput,
  MemberMappingMemberOption,
} from 'types/api/SystemTenant/AROCube/MemberMappingDefinitionTypes';

const base = '/api/v1/member-mapping-definition';
export const MEMBER_MAPPING_QUERY_KEY = {
  LIST: 'MEMBER_MAPPING_DEFINITIONS',
  DETAILS: 'MEMBER_MAPPING_DEFINITION',
  MEMBERS: 'MEMBER_MAPPING_MEMBERS',
};
export function useMemberMappingDefinitions(sourceMemberId?: string, destinationMemberId?: string, enabled = true) {
  return useQuery<AxiosResponse<MemberMappingDefinition[]>, AxiosDefaultErrorEntity>(
    [MEMBER_MAPPING_QUERY_KEY.LIST, sourceMemberId, destinationMemberId],
    () => axios.get(`${base}/list?${qs.stringify({ sourceMemberId, destinationMemberId })}`),
    { enabled }
  );
}
export function useMemberMappingDefinition(id?: string | null, enabled = true) {
  return useQuery<AxiosResponse<MemberMappingDefinition>, AxiosDefaultErrorEntity>(
    [MEMBER_MAPPING_QUERY_KEY.DETAILS, id],
    () => axios.get(`${base}/${encodeURIComponent(id!)}`),
    { enabled: enabled && !!id }
  );
}
export function useMemberMappingMembers(enabled = true) {
  return useQuery<AxiosResponse<MemberMappingMemberOption[]>, AxiosDefaultErrorEntity>(
    [MEMBER_MAPPING_QUERY_KEY.MEMBERS],
    () => axios.get(`${base}/members`),
    { enabled }
  );
}
export function useSaveMemberMappingDefinition() {
  const client = useQueryClient();
  return useMutation<AxiosResponse<MemberMappingDefinition>, AxiosDefaultErrorEntity, MemberMappingDefinitionInput>(
    (input) => axios.post(`${base}/upsert`, input),
    {
      retry: false,
      onSuccess: (response) => {
        client.setQueryData([MEMBER_MAPPING_QUERY_KEY.DETAILS, response.data.id], response);
        client.invalidateQueries(MEMBER_MAPPING_QUERY_KEY.LIST);
      },
    }
  );
}
export function useDeleteMemberMappingDefinition() {
  const client = useQueryClient();
  return useMutation<AxiosResponse, AxiosDefaultErrorEntity, string>(
    (id) => axios.delete(`${base}/${encodeURIComponent(id)}`),
    {
      retry: false,
      onSuccess: () => {
        client.invalidateQueries(MEMBER_MAPPING_QUERY_KEY.LIST);
        client.invalidateQueries(MEMBER_MAPPING_QUERY_KEY.DETAILS);
      },
    }
  );
}
export function usePreviewMemberMappingDefinition() {
  return useMutation<
    AxiosResponse<CubeMemberMatchPreviewResponse>,
    AxiosDefaultErrorEntity,
    MemberMappingDefinitionInput
  >((input) => axios.post(`${base}/preview`, input), { retry: false });
}
